'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import PageType from '@alltrails/amplitude/enums/PageType';
import logDirectionsButtonTapped from '@alltrails/amplitude/events/logDirectionsButtonTapped';
import logPrintMapButtonClicked from '@alltrails/amplitude/events/logPrintMapButtonClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import IconButton from '@alltrails/denali/components/IconButton';
import Car from '@alltrails/denali/icons/Car';
import Print from '@alltrails/denali/icons/Print';
import Share from '@alltrails/denali/icons/Share';
import type { ShareObject } from '@alltrails/sharing/types/ShareObject';
import { useModal } from '../../ShareModal/ShareModalContext';
import styles from './DesktopButtons.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type Props = {
  directionsLink?: string;
  printLink?: string;
  shareObject: ShareObject;
  id: number;
  pageType?: PageType;
};

const DesktopButtons = ({ directionsLink, printLink, shareObject, id, pageType }: Props) => {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const { openModal } = useModal();
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();

  return (
    <div className={styles.desktopButtonsContainer}>
      {printLink && (
        <div className={styles.iconContainer}>
          <IconButton
            icon={{ Component: Print }}
            linkInfo={{ href: wrapUrlSafe(printLink, languageRegionCode) }}
            onClick={() => logPrintMapButtonClicked({ map_location: 'location page', trail_id: id })}
            title={intl.formatMessage({ defaultMessage: 'Print map' })}
            testId="location-page-print"
            size="md"
          />
        </div>
      )}
      {shareObject && (
        <IconButton
          icon={{ Component: Share }}
          onClick={openModal}
          title={intl.formatMessage({ defaultMessage: 'Share' })}
          testId="location-page-share"
          size="md"
        />
      )}
      {directionsLink && (
        <IconButton
          icon={{ Component: Car }}
          {...(user ? { linkInfo: { href: directionsLink, target: '_blank', rel: ['noopener', 'noreferrer'] } } : {})}
          onClick={() => {
            logDirectionsButtonTapped({ id, page_type: pageType });

            if (!user) {
              setIsSignUpModalOpen(true);
            }
          }}
          title={intl.formatMessage({ defaultMessage: 'Directions' })}
          testId="location-page-directions"
          size="md"
        />
      )}
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            window.open(directionsLink, '_blank', 'noopener noreferrer');
          }}
          trigger="Location Directions"
          upsellTrigger="Location Directions"
        />
      )}
    </div>
  );
};

export default DesktopButtons;
